// AI workspace operations — let the assistant DO what a user can do in the UI.
//
// The simone assistant is a chat alternative to the workspace UI: anything a
// user can do by clicking, the assistant should be able to do by calling a tool
// (instead of telling the user to do it manually). These thin wrappers take
// LLM-friendly flat args, resolve the target entity by id OR name from the live
// unified root state (the same lists the page snapshot shows the model), and
// reuse the EXISTING UI functions / SDK routes — so each op runs the same code
// path (optimistic state update + SDK persist) the UI uses and the change shows
// immediately.
//
// Registered via functions/index.js; invoked from the server's clientTool path
// (simoneAiTransport `_runPlatformTool` → el.call). `this` is the workspace shell
// root element (window.__wsRoot): `this.state.root` is the unified state,
// `this.call(fn,…)` reaches every registered package function, `this.sdk` / the
// getSDK() proxy is the SDK.

// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

const norm = (v) =>
  String(v == null ? '' : v)
    .trim()
    .toLowerCase()
const ok = (extra) => ({ ok: true, ...extra })
const fail = (message) => ({ ok: false, error: message })

// Resolve an entity from a root list by exact id (id/ticketId/number), then by
// case-insensitive label (name/title/…). The AI normally passes an id from the
// page snapshot, but tolerate a name so "duplicate My notes" works directly.
function resolve(list, target, nameKeys = ['name', 'title']) {
  if (!Array.isArray(list) || target == null || target === '') return null
  const t = String(target)
  const byId = list.find(
    (x) => String(x?.id) === t || String(x?.ticketId) === t || String(x?.number) === t
  )
  if (byId) return byId
  const tn = norm(target)
  return list.find((x) => nameKeys.some((k) => norm(x?.[k]) === tn)) || null
}

const rowId = (res) => {
  const row = res?.data || res
  if (!row) return null
  return row.id || (Array.isArray(row) ? row[0] && row[0].id : null)
}

// ── FILES (root.fileNodes; SDK entity workspaceProject.fileCanvas) ──────────

export const aiCreateFolder = async function (name) {
  const id = await this.call('createCanvasFolder', name || 'New folder')
  return id
    ? ok({ created: { kind: 'folder', id, name: name || 'New folder' } })
    : fail('Could not create the folder.')
}

export const aiRenameFile = async function (target, newName) {
  const node = resolve(this.state.root?.fileNodes, target)
  if (!node) return fail(`No file or folder named "${target}" found.`)
  if (!newName) return fail('A new name is required.')
  const renamed = await this.call('commitRenameNode', node.id, String(newName))
  if (renamed === false) return fail(`Could not rename "${node.name}".`)
  return ok({ renamed: { id: node.id, from: node.name, to: String(newName) } })
}

export const aiDeleteFile = async function (target) {
  const node = resolve(this.state.root?.fileNodes, target)
  if (!node) return fail(`No file or folder named "${target}" found.`)
  const deleted = await this.call('deleteCanvasNode', node.id)
  if (deleted === false) return fail(`Could not delete "${node.name}".`)
  return ok({ deleted: { id: node.id, name: node.name } })
}

export const aiDuplicateFile = async function (target, newName) {
  const root = this.state.root
  const node = resolve(root?.fileNodes, target)
  if (!node) return fail(`No file or folder named "${target}" found.`)
  const name = (newName && String(newName).trim()) || `${node.name} copy`
  // Reuse the same blob (file_id/url) — duplicating the row, not the storage object.
  const payload = {
    kind: node.kind || 'file',
    name,
    parent_id: node.parentId || null,
    x: (Number(node.x) || 0) + 24,
    y: (Number(node.y) || 0) + 24,
    scope: node.scope || root.filesScope || 'shared',
    created_by: root.currentUserEmail || null,
    file_id: node.fileId || null,
    url: node.url || null,
    mime_type: node.mime || null,
    size: node.size || null,
    doc_id: node.docId || null
  }
  let id
  try {
    id = rowId(await shellGetSDK().execute('workspaceProject.fileCanvas', 'create', { payload }))
  } catch (e) {
    return fail('Duplicate failed: ' + (e?.message || e))
  }
  if (!id) return fail('Duplicate failed (no id returned).')
  try {
    root.update({
      fileNodes: [...(root.fileNodes || []), { ...node, id, name, x: payload.x, y: payload.y }]
    })
  } catch (_) {}
  return ok({ duplicated: { from: node.name, to: name, id } })
}

export const aiMoveFileToFolder = async function (target, folderTarget) {
  const root = this.state.root
  const node = resolve(root?.fileNodes, target)
  if (!node) return fail(`No file or folder named "${target}" found.`)
  let folderId = null
  if (folderTarget && !['root', 'desktop', 'top', 'home'].includes(norm(folderTarget))) {
    const folder = resolve(
      (root?.fileNodes || []).filter((n) => n.kind === 'folder'),
      folderTarget
    )
    if (!folder) return fail(`No folder named "${folderTarget}" found.`)
    folderId = folder.id
  }
  await this.call('moveNodesIntoFolder', [node.id], folderId)
  return ok({ moved: { id: node.id, into: folderId || 'desktop' } })
}

// ── DOCUMENTS / NOTES (root.notes / root.documents; SDK entity docs.notes/docs) ──

export const aiCreateNote = async function (title, content, folder) {
  // `source: 'ai'` is what the docs schema expects for an assistant-authored
  // row (member / import / meet / ai are all first-class). Without it every
  // note the assistant writes is indistinguishable from one a person typed —
  // measured live: AI-created docs were all landing as source 'member'.
  // `folderPath` lets a multi-document run file itself into one folder instead
  // of scattering across the root; createNote has always accepted both.
  const fallbackTitle = this.call('polyglot', 'workspace.ai.newRecord.note')
  const id = await this.call('createNote', {
    title: title || fallbackTitle,
    content: content || '',
    source: 'ai',
    ...(folder ? { folderPath: String(folder) } : {})
  })
  return id
    ? ok({ created: { kind: 'note', id, title: title || fallbackTitle } })
    : fail('Could not create the note.')
}

export const aiRenameNote = async function (target, newTitle) {
  const root = this.state.root
  const inNotes = resolve(root?.notes, target, ['title', 'name'])
  const doc = inNotes || resolve(root?.documents, target, ['title', 'name'])
  if (!doc) return fail(`No note or document named "${target}" found.`)
  if (!newTitle) return fail('A new title is required.')
  const entity = inNotes ? 'docs.notes' : 'docs'
  try {
    await shellGetSDK().execute(entity, 'update', {
      id: doc.id,
      payload: { title: String(newTitle) }
    })
  } catch (e) {
    return fail('Rename failed: ' + (e?.message || e))
  }
  try {
    const patch = (arr) =>
      (arr || []).map((x) =>
        String(x.id) === String(doc.id) ? { ...x, title: String(newTitle) } : x
      )
    root.update({ notes: patch(root.notes), documents: patch(root.documents) })
  } catch (_) {}
  // The cached note lists predate the write (documents functions/docListCache.js).
  this.call('forgetDocLists')
  return ok({ renamed: { id: doc.id, from: doc.title || doc.name, to: String(newTitle) } })
}

export const aiDeleteNote = async function (target) {
  const root = this.state.root
  const inNotes = resolve(root?.notes, target, ['title', 'name'])
  const doc = inNotes || resolve(root?.documents, target, ['title', 'name'])
  if (!doc) return fail(`No note or document named "${target}" found.`)
  // The typed `docs.notes` sugar only does CRU; the generic `docs` entity owns
  // remove for both notes and documents.
  try {
    await shellGetSDK().execute('docs', 'remove', { id: doc.id })
  } catch (e) {
    return fail('Delete failed: ' + (e?.message || e))
  }
  try {
    const drop = (arr) => (arr || []).filter((x) => String(x.id) !== String(doc.id))
    root.update({ notes: drop(root.notes), documents: drop(root.documents) })
  } catch (_) {}
  this.call('forgetDocLists')
  return ok({ deleted: { id: doc.id, title: doc.title || doc.name } })
}

// ── TICKETS (root.tickets + root.columns; SDK entity tickets) ───────────────

// Fuzzy-normalize a column key/label/name for matching: lowercase, strip
// every non-alphanumeric (so "To Do" / "to-do" / "TO_DO" collapse to the
// same "todo"), then drop one trailing "s" (naive singularize) so a plural
// board heading — "To Dos" / "Backlogs" / "TO DOS" — still matches its
// singular column ("todo" / "backlog"). AI-supplied column labels rarely
// match the stored label byte-for-byte (case, spacing, plural), and the
// straight equality this replaced silently fell through to the caller's
// "column not found → default" branch, landing every mismatched ticket in
// Backlog regardless of what the user asked for.
const normColumnFuzzy = (v) => {
  const base = String(v == null ? '' : v)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
  return base.length > 1 && base.endsWith('s') ? base.slice(0, -1) : base
}

// Resolve a column by key OR human label from root.columns; returns its key.
// Tries an exact normalized match first, then falls back to the fuzzy
// (punctuation/case/plural-insensitive) match above — only truly unmatched
// labels fall through to the caller's default-column behavior.
// Exported for unit testing (see columnKeyFor.test.js).
export function columnKeyFor(root, columnRef) {
  const cols = root?.columns || []
  const exact = cols.find(
    (c) =>
      norm(c?.key) === norm(columnRef) ||
      norm(c?.label) === norm(columnRef) ||
      norm(c?.name) === norm(columnRef)
  )
  if (exact) return { col: exact, key: exact.key }
  const target = normColumnFuzzy(columnRef)
  const col = cols.find(
    (c) =>
      normColumnFuzzy(c?.key) === target ||
      normColumnFuzzy(c?.label) === target ||
      normColumnFuzzy(c?.name) === target
  )
  return { col, key: col?.key || columnRef }
}

export const aiMoveTicket = async function (target, columnRef) {
  const root = this.state.root
  const tk = resolve(root?.tickets, target, ['title'])
  if (!tk) return fail(`No ticket matching "${target}" found.`)
  const { col, key } = columnKeyFor(root, columnRef)
  const res = await this.call('moveTicket', {
    ticketId: tk.ticketId || tk.id,
    columnKey: key,
    position: 0
  })
  if (res === false) return fail('Move failed.')
  try {
    root.update({
      tickets: (root.tickets || []).map((x) =>
        String(x.id) === String(tk.id) ? { ...x, columnKey: key } : x
      )
    })
  } catch (_) {}
  return ok({ moved: { ticket: tk.title, to: col?.label || key } })
}

// AI-306 (tickets/ai.md) — Undo an AI-created item BY ID, with zero
// page-state coupling. The sibling aiDelete* ops resolve by name against the
// CURRENTLY-LOADED root arrays (root.notes/documents/tickets), which is right
// for the model's "delete the launch memo" but wrong for the timeline's Undo
// chip: the chip carries the exact id from the create result, and the arrays
// are empty on any route that hasn't loaded them — measured live: an Undo on
// /w/<org> "succeeded" in the UI while the doc stayed in Mongo, because the
// name-resolve failed before any delete ran. Deletes by id through the same
// persist paths the resolving ops use; nothing new is reachable.
// A failed Undo also carries `cause`: a code the timeline row says in words
// of the active language (ChatToolRow UndoFailure → ai.tool.undoWhy.*). Wave 2
// (dev 2026-10-28): the row printed the raw cause, an SDK message or
// "deleteEvent is not available here", in English on a Georgian page. The raw
// error goes to the console only. The code comes from the error's structured
// fields, never from its words:
//   gone         HTTP 404 / 410: the item is not there any more
//   denied       HTTP 401 / 403: the person may not delete it
//   network      the SDK's NETWORK_UNREACHABLE: the request never arrived
//   unavailable  DELETE_UNAVAILABLE: this page has no delete for the kind
//   failed       anything else
const undoFailed = (err) => {
  console.warn('[aiUndoCreated] Undo failed:', err)
  const status = Number(err && err.status)
  const code = err && err.code
  const cause =
    status === 404 || status === 410
      ? 'gone'
      : status === 401 || status === 403
        ? 'denied'
        : code === 'NETWORK_UNREACHABLE'
          ? 'network'
          : code === 'DELETE_UNAVAILABLE'
            ? 'unavailable'
            : 'failed'
  return { ...fail('Undo failed.'), cause }
}
export const aiUndoCreated = async function (kind, id) {
  const k = String(kind || '')
  const target = String(id || '').trim()
  if (!target) return fail('An item id is required.')
  if (k === 'note' || k === 'document') {
    try {
      await shellGetSDK().execute('docs', 'remove', { id: target })
    } catch (e) {
      return undoFailed(e)
    }
    try {
      const root = this.state.root
      const drop = (arr) => (arr || []).filter((x) => String(x.id) !== target)
      root.update({ notes: drop(root.notes), documents: drop(root.documents) })
    } catch (_) {}
    this.call('forgetDocLists')
    return ok({ deleted: { kind: k, id: target } })
  }
  // A ticket and an event — made by the client op or by a server-run create
  // (lane TOOLS D2: its result carries the created id) — go through the
  // registered delete of their surface (tickets / calendar eager slices,
  // declarative fetch → the SDK). An event delete is the calendar's soft
  // delete, so a synced Google copy is removed too. `callerReports`: the
  // delete throws its failure here, its status and code intact, and shows no
  // message of its own. The row says the result, once.
  if (k === 'ticket' || k === 'event') {
    const fnName = k === 'ticket' ? 'deleteTicket' : 'deleteEvent'
    if (typeof this.context?.functions?.[fnName] !== 'function') {
      return undoFailed(
        Object.assign(new Error(`${fnName} is not available here`), {
          code: 'DELETE_UNAVAILABLE'
        })
      )
    }
    let res
    try {
      res = await this.call(fnName, target, { callerReports: true })
    } catch (e) {
      return undoFailed(e)
    }
    if (res === false) return undoFailed(`${fnName} refused ${target}`)
    const key = k === 'ticket' ? 'tickets' : 'calendarEvents'
    try {
      const root = this.state.root
      root.update({ [key]: (root[key] || []).filter((x) => String(x.id) !== target) })
    } catch (_) {}
    return ok({ deleted: { kind: k, id: target } })
  }
  return fail(`"${k}" cannot be undone from the timeline.`)
}

export const aiDeleteTicket = async function (target) {
  const root = this.state.root
  const tk = resolve(root?.tickets, target, ['title'])
  if (!tk) return fail(`No ticket matching "${target}" found.`)
  const res = await this.call('deleteTicket', tk.ticketId || tk.id)
  if (res === false) return fail('Delete failed.')
  try {
    root.update({ tickets: (root.tickets || []).filter((x) => String(x.id) !== String(tk.id)) })
  } catch (_) {}
  return ok({ deleted: { ticket: tk.title, id: tk.id } })
}

export const aiCreateTicket = async function (title, columnRef, body) {
  const root = this.state.root
  if (!title) return fail('A ticket title is required.')
  const cols = root?.columns || []
  const { col, key } = columnRef
    ? columnKeyFor(root, columnRef)
    : { col: cols[0], key: cols[0]?.key }
  const columnKey = key || 'backlog'
  const ticket = {
    title: String(title),
    body: body || '',
    columnKey,
    reporterEmail: root.currentUserEmail || null,
    state: 'not_started'
  }
  const id = await this.call('saveTicket', ticket)
  if (!id) return fail('Could not create the ticket.')
  // saveTicket is a PURE persist — unlike the sibling ops it does NOT
  // touch root.tickets, so an AI-created ticket never appeared on the board and
  // selectPageItem/open couldn't find it right after. Optimistically insert the
  // new row (the same thing the board's own create flow does) so it shows
  // immediately AND is openable in the same turn. Guard a double-insert if a
  // board reload raced in first.
  try {
    if (!(root.tickets || []).some((t) => String(t.id) === String(id))) {
      root.update({
        tickets: [
          {
            id,
            title: ticket.title,
            body: ticket.body,
            columnKey,
            column_key: columnKey,
            position: 0,
            reporterEmail: ticket.reporterEmail,
            reporter_email: ticket.reporterEmail,
            state: ticket.state,
            labels: [],
            metadata: {}
          },
          ...(root.tickets || [])
        ]
      })
    }
  } catch (_) {}
  // Return the id so the assistant can chain create → select_page_item(open)
  // without a re-query round-trip.
  return ok({
    created: { kind: 'ticket', id, title: ticket.title, column: col?.label || columnKey }
  })
}

// update_ticket — rewrite title and/or body of an existing ticket. null means
// "leave unchanged" (the server tool marshals absent fields to null). Reuses
// saveTicket (the board's own persist path), which shapes the update payload
// and lands the authoritative doc back into state.
export const aiUpdateTicket = async function (target, title, body) {
  const root = this.state.root
  const tk = resolve(root?.tickets, target, ['title'])
  if (!tk) return fail(`No ticket matching "${target}" found.`)
  if (title == null && body == null) {
    return fail('Nothing to update — pass a new title and/or body.')
  }
  const patch = {
    ...(title == null ? {} : { title: String(title) }),
    ...(body == null ? {} : { body: String(body) })
  }
  const saved = await this.call('saveTicket', {
    ...tk,
    id: tk.ticketId || tk.id,
    ticketId: tk.ticketId || tk.id,
    ...patch
  })
  if (!saved) return fail('Could not update the ticket.')
  try {
    root.update({
      tickets: (root.tickets || []).map((x) =>
        String(x.id) === String(tk.id) ? { ...x, ...patch } : x
      )
    })
  } catch (_) {}
  return ok({
    updated: {
      kind: 'ticket',
      id: tk.id,
      title: patch.title || tk.title,
      fields: Object.keys(patch)
    }
  })
}

// assign_ticket — set (or clear, with an empty email) the assignee. Validates
// the address against the live people list when it is loaded, so the model
// gets a factual refusal instead of a silent no-op for a non-member.
export const aiAssignTicket = async function (target, email) {
  const root = this.state.root
  const tk = resolve(root?.tickets, target, ['title'])
  if (!tk) return fail(`No ticket matching "${target}" found.`)
  const addr = String(email || '').trim()
  if (addr) {
    const people = Array.isArray(root?.people) ? root.people : []
    if (people.length && !people.some((p) => norm(p?.email) === norm(addr))) {
      return fail(
        `"${addr}" is not a member of this workspace — use an email from list_members, or invite them first.`
      )
    }
  }
  const saved = await this.call('saveTicket', {
    ...tk,
    id: tk.ticketId || tk.id,
    ticketId: tk.ticketId || tk.id,
    assigneeEmail: addr || null
  })
  if (!saved) return fail('Could not update the assignee.')
  try {
    root.update({
      tickets: (root.tickets || []).map((x) =>
        String(x.id) === String(tk.id)
          ? { ...x, assigneeEmail: addr || null, assignee_email: addr || null }
          : x
      )
    })
  } catch (_) {}
  return ok({
    assigned: { kind: 'ticket', id: tk.id, title: tk.title, assignee: addr || null }
  })
}

// ── CHAT (root.channels; SDK entity workspaceProject.chat) ──────────────────

export const aiCreateChannel = async function (name, topic, isPrivate) {
  if (!name) return fail('A channel name is required.')
  // createChannel lives in the lazily-folded chat context (bundle-fold) —
  // headless AI ops must fold before calling.
  await this.call('ensureChatContext')
  const id = await this.call('createChannel', {
    kind: 'channel',
    name: String(name),
    topic: topic || '',
    isPrivate: !!isPrivate
  })
  return id
    ? ok({ created: { kind: 'channel', id, name: String(name) } })
    : fail('Could not create the channel.')
}

export const aiSendMessage = async function (channelTarget, body) {
  const root = this.state.root
  if (!body) return fail('A message body is required.')
  const ch = resolve(root?.channels, channelTarget, ['name', 'title'])
  // Only trust a raw channelTarget when it LOOKS like an id (Mongo hex / uuid) —
  // an unresolvable NAME must NOT be used verbatim as a channel id, which would
  // misroute the message into an unrendered bucket (or another workspace). Fall
  // back to the active channel, else fail with a clear "no channel" error.
  const looksLikeId = (v) =>
    typeof v === 'string' && (/^[0-9a-f]{24}$/i.test(v) || /^[0-9a-f-]{32,}$/i.test(v))
  const channelId =
    (ch && ch.id) || (looksLikeId(channelTarget) ? channelTarget : null) || root?.activeChannelId
  if (!channelId) return fail(`No channel matching "${channelTarget}" found.`)
  // sendMessage rides the lazily-folded chat context (bundle-fold).
  await this.call('ensureChatContext')
  const id = await this.call('sendMessage', { channelId, body: String(body) })
  return id ? ok({ sent: { channel: ch?.name || channelId } }) : fail('Could not send the message.')
}

// ── AI-CREATED home widgets (tickets/opus.md "Per-workspace AI-CREATED
// widgets") ─────────────────────────────────────────────────────────────────
//
// The second half of Nika's mandate "new apps should be able to have their own
// widgets from appstore or created by user ai": a member asks the assistant for
// a widget and it lands on their home board with the SAME chrome, hue and
// gs-clamps an app-store widget gets.
//
// These are ordinary WRITE data-ops — same registry, same `runClientToolWithAuth`
// → `resolveAiToolApproval` gate every other write goes through (Ask-first by
// default), no bespoke approval path. What makes them safe is not this file:
//
//   * the TOOL SCHEMA (server workspaceTools.js `create_widget`) accepts only
//     the fixed block vocabulary — a model that "wants" to emit a function
//     literally cannot, because no field accepts one;
//   * the SERVER validates every write against that vocabulary
//     (widgetDefSchema.js) and rejects anything else;
//   * the SERVER capability-checks the dataRecipe for the CREATING user before
//     it persists, and re-checks it for every VIEWING user on read.
//
// So this layer stays thin on purpose: SDK call, refresh the plane, report.

// Re-read the AI widget plane after a write so the board converges in the same
// turn (the create/edit/delete is only "done" when the user can see it).
const _refreshAiWidgets = async function (root) {
  const sdk = shellGetSDK()
  const wsId = (root && root.workspace && (root.workspace.id || root.workspace._id)) || null
  const rows = await sdk.execute(
    'workspaceProject.widgetDefs',
    'list',
    wsId ? { workspaceId: wsId } : {}
  )
  root.update({ workspaceAiWidgets: Array.isArray(rows) ? rows : [] })
  return Array.isArray(rows) ? rows : []
}

export const aiCreateWidget = async function (widget, opts) {
  const root = this.state.root
  const def = widget && typeof widget === 'object' ? widget : null
  if (!def || !def.title) return fail('A widget title is required.')
  // A PINNED board card (DESIGN-WL-ASSISTANT-MAIN-HOME-1; the widgetDefs
  // contract CORE-WIDGETDEFS-ACCEPT-BOARD-ITEMS-1) carries either an installed
  // app's widget by reference with its params (`{ widget: '<app>.<id>',
  // params }`) or the values the card showed (`{ block: { type, data, format?,
  // source? } }`) — no recipe of its own. The server derives the kind and
  // validates both shapes; this guard keeps refusing a def with no content.
  const pinnedRef = typeof def.widget === 'string' && !!def.widget
  const pinnedBlock = !!(def.block && typeof def.block === 'object' && def.block.type)
  // A RECORDS block (WIDGET-TOOLS-RECORDS-SHAPE-1): one app collection read
  // live for each viewer — the assistant's create_widget `records` shape and a
  // pinned board records card. The server validates it against the creating
  // member's collections; the home draws it with AssistantRecordsBlock.
  const pinnedRecords = !!(
    def.records &&
    typeof def.records === 'object' &&
    typeof def.records.collection === 'string' &&
    def.records.collection
  )
  if (!pinnedRef && !pinnedBlock && !pinnedRecords) {
    if (!def.dataRecipe || !def.dataRecipe.from) {
      return fail('A widget needs a dataRecipe naming which data it reads.')
    }
    if (!def.body || !Array.isArray(def.body.blocks) || !def.body.blocks.length) {
      return fail('A widget needs a body with at least one block.')
    }
  }
  const sdk = shellGetSDK()
  const wsId = (root && root.workspace && (root.workspace.id || root.workspace._id)) || null
  try {
    const created = await sdk.execute('workspaceProject.widgetDefs', 'create', {
      widget: def,
      ...(wsId ? { workspaceId: wsId } : {})
    })
    await _refreshAiWidgets.call(this, root)
    const createdId = created && created.id
    // "Change with AI" (HOME-WIDGET-CHANGE-WITH-AI-1): the new card takes the
    // place of the card the member asked to change — the home board's own
    // operation (pages/main.js _homeOps.replaceWidget). Off the home board the
    // widget is still created; the answer says the card was not swapped.
    const replaces = opts && typeof opts.replaces === 'string' ? opts.replaces.trim() : ''
    let replaced = false
    if (replaces && createdId) {
      const ops = root && root._homeOps
      try {
        replaced = !!(
          ops &&
          typeof ops.replaceWidget === 'function' &&
          ops.replaceWidget(replaces, createdId)
        )
      } catch (_) {
        replaced = false
      }
    }
    return ok({
      created: {
        kind: 'widget',
        id: createdId,
        title: def.title,
        ...(replaces ? { replaced: replaced ? replaces : null } : {})
      }
    })
  } catch (err) {
    // The server's rejection is the useful part — a validation failure names
    // the field of the vocabulary the model got wrong, a capability failure
    // tells the user plainly that they cannot read that data. The SDK keeps
    // the body on `err.cause`: its `details` carry the field reasons, and
    // without them the model only ever saw "widget definition rejected" and
    // could not correct itself (dev 2026-10-04: dataRecipe.params.filter).
    const base = (err && err.message) || 'Could not create the widget.'
    const details = err && err.cause && Array.isArray(err.cause.details) ? err.cause.details : []
    const why = details
      .slice(0, 6)
      .map((d) => (d && d.path ? `${d.path}: ${d.message}` : (d && d.message) || ''))
      .filter(Boolean)
    return fail(why.length ? `${base} (${why.join('; ')})` : base)
  }
}

export const aiEditWidget = async function (widgetId, patch) {
  const root = this.state.root
  const id = String(widgetId || '').trim()
  if (!id) return fail('A widget id is required.')
  if (!patch || typeof patch !== 'object') return fail('Nothing to change.')
  const sdk = shellGetSDK()
  const wsId = (root && root.workspace && (root.workspace.id || root.workspace._id)) || null
  try {
    const updated = await sdk.execute('workspaceProject.widgetDefs', 'update', {
      id,
      widget: patch,
      ...(wsId ? { workspaceId: wsId } : {})
    })
    await _refreshAiWidgets.call(this, root)
    return ok({ updated: { kind: 'widget', id: (updated && updated.id) || id } })
  } catch (err) {
    // Same as aiCreateWidget: the field reasons ride on `err.cause.details`.
    const base = (err && err.message) || 'Could not update the widget.'
    const details = err && err.cause && Array.isArray(err.cause.details) ? err.cause.details : []
    const why = details
      .slice(0, 6)
      .map((d) => (d && d.path ? `${d.path}: ${d.message}` : (d && d.message) || ''))
      .filter(Boolean)
    return fail(why.length ? `${base} (${why.join('; ')})` : base)
  }
}

export const aiDeleteWidget = async function (widgetId) {
  const root = this.state.root
  const id = String(widgetId || '').trim()
  if (!id) return fail('A widget id is required.')
  const sdk = shellGetSDK()
  const wsId = (root && root.workspace && (root.workspace.id || root.workspace._id)) || null
  try {
    await sdk.execute('workspaceProject.widgetDefs', 'remove', {
      id,
      ...(wsId ? { workspaceId: wsId } : {})
    })
    await _refreshAiWidgets.call(this, root)
    return ok({ deleted: { kind: 'widget', id } })
  } catch (err) {
    return fail((err && err.message) || 'Could not delete the widget.')
  }
}
